/**
 * ✅ FASE 11: Componente para notificar sobre atualizações PWA
 * Com detecção de erros de chunk e força atualização
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { clearAllCaches } from '@/utils/versionCheck';
import { reportChunkError } from '@/utils/errorReporter';

export function PWAUpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isChunkError, setIsChunkError] = useState(false);

  useEffect(() => {
    // Detectar erros de chunk
    const handleError = async (event: ErrorEvent) => {
      const error = event.error || event.message;
      const errorMsg = typeof error === 'string' ? error : error?.message || '';
      
      if (
        errorMsg.includes('Failed to fetch dynamically imported module') ||
        errorMsg.includes('Importing a module script failed')
      ) {
        console.error('🔴 Chunk error detectado no PWAUpdatePrompt');
        setIsChunkError(true);
        setShowPrompt(true);
        
        // Reportar erro
        await reportChunkError(errorMsg, {
          component: 'PWAUpdatePrompt',
          action: 'showing_update_prompt'
        });
      }
    };
    
    window.addEventListener('error', handleError);

    // Verificar se há service worker registrado
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // Verificar atualizações periodicamente
        const updateInterval = setInterval(() => {
          registration.update();
        }, 30 * 60 * 1000); // 30 minutos

        // Listener para atualizações
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setShowPrompt(true);
              }
            });
          }
        });
        
        return () => clearInterval(updateInterval);
      });
    }
    
    return () => window.removeEventListener('error', handleError);
  }, []);

  const handleUpdate = async () => {
    console.log('🔄 Iniciando atualização...');
    await clearAllCaches();
    window.location.reload();
  };

  const handleDismiss = () => {
    if (!isChunkError) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed top-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96 animate-in slide-in-from-top">
      <Card className={isChunkError ? "border-destructive/50 shadow-lg" : "border-primary/20 shadow-lg"}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
              isChunkError ? 'bg-destructive/10' : 'bg-primary/10'
            }`}>
              {isChunkError ? (
                <AlertTriangle className="w-6 h-6 text-destructive" />
              ) : (
                <RefreshCw className="w-6 h-6 text-primary" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm mb-1">
                {isChunkError ? 'Atualização Necessária' : 'Atualização Disponível'}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {isChunkError 
                  ? 'Detectamos um problema. Por favor, atualize a aplicação para continuar.'
                  : 'Uma nova versão do app está disponível. Atualize para aproveitar as melhorias.'
                }
              </p>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleUpdate}
                  className="flex-1"
                  variant={isChunkError ? "destructive" : "default"}
                >
                  Atualizar Agora
                </Button>
                {!isChunkError && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDismiss}
                  >
                    Depois
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
